import React from 'react';
import { A3Seite } from './A3Seite';
import { Abschnitt, AbschnittLeer, AbschnittTabelle, AbschnittZeile, AbschnittZelle } from './Abschnitt';
import { HANDBUCH_FARBEN } from './stil';
import { formatDatum } from '../../../services/assignmentLogic';
import type { Planfassung } from '../../../services/handbuchService';
import type { Handbuch, HistorieEintrag } from '../types';

/**
 * Kapitel „Änderungshistorie" — welche Fassung wann und von wem entstand.
 *
 * Portiert aus `pages/editor/HistoriePage.tsx` der Quell-App (Tabelle Version /
 * Datum / Autor / Änderungen, neueste zuerst). Zwei Ergänzungen, beide mit
 * Grund:
 *
 * 1. **Die Planfassungen stehen daneben.** Ein Handbuch beurteilt einen
 *    Bauablauf; ändert sich der Plan, ist die Beurteilung möglicherweise
 *    überholt. Die Fassungen aus `sige_projekte` (gleiche `project_id`,
 *    `version_label`, `created_at`) zeigen genau das — und zwar getrennt von
 *    der eigenen Historie, denn es sind zwei Dokumente mit zwei Zählungen.
 * 2. **Der Freigabestand steht im Kopf.** Entscheidung Nr. 8: die Freigabe
 *    friert die Kapitel ein. Eine spätere Änderung erzeugt eine NEUE Fassung
 *    in `historie` und überschreibt die freigegebene nicht — genau deshalb ist
 *    diese Seite nicht Beiwerk, sondern der Nachweis, dass nichts rückwirkend
 *    verändert wurde.
 *
 * Die Seite SCHREIBT NICHTS. `historie` wird an genau einer Stelle fortge-
 * schrieben (`ergaenzeHistorie` in `handbuchLogic.ts`, ausgeführt vom Dienst).
 */

// ═══════════════════════════════════════════════════════════════════════════
// REINE FUNKTIONEN
//
// Gehören fachlich nach `handbuchLogic.ts` — dort ist die geprüfte Stelle (es
// gibt kein jsdom). Sie stehen hier oben und sind ausdrücklich NICHT
// exportiert: `react-refresh/only-export-components` lässt in einer
// Komponentendatei keine Wertexporte zu.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Historie, neueste zuerst.
 *
 * Verglichen wird LEXIKALISCH über den ISO-String und nicht über `Date`: der
 * String-Konstruktor legt „2026-01-01" als Mitternacht UTC aus, und in einer
 * negativen Zeitzone kippt daraus beim Zurückrechnen der 31.12. Bei gleichem
 * Datum entscheidet die Fassungsnummer absteigend, sonst stünde „2.0" unter
 * „1.0", wenn beides am selben Tag geschah.
 */
const sortiereHistorie = (historie: HistorieEintrag[]): HistorieEintrag[] =>
    [...(historie || [])].sort((a, b) => {
        const da = (a?.datum || '').slice(0, 10);
        const db = (b?.datum || '').slice(0, 10);
        if (da !== db) return da < db ? 1 : -1;
        const na = Number.parseFloat(a?.nr || '0');
        const nb = Number.parseFloat(b?.nr || '0');
        if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return nb - na;
        return 0;
    });

/**
 * Planfassungen, neueste zuerst.
 *
 * `created_at` ist ein Zeitstempel; für die Sortierung genügt der lexikalische
 * Vergleich, weil ISO-8601-Zeitstempel derselben Zone in Textreihenfolge auch
 * in Zeitreihenfolge stehen. Umgerechnet wird nichts — die Spalte zeigt den
 * Tag, und einen Tag darf keine Zeitzone verschieben.
 */
const sortierePlanfassungen = <T extends { created_at?: string }>(fassungen: T[]): T[] =>
    [...(fassungen || [])].sort((a, b) => {
        const ca = a?.created_at || '';
        const cb = b?.created_at || '';
        return ca < cb ? 1 : ca > cb ? -1 : 0;
    });

/** Fassungsname einer Planzeile. `version_label` ist frei vergeben und darf
 *  leer sein — dann bleibt die Plannummer, und wenn auch die fehlt, wird das
 *  gesagt statt eine Zelle leer zu lassen. */
const fassungsName = (plan: { plan_nr?: string; version_label?: string | null }): string =>
    (plan.version_label || '').trim()
    || (plan.plan_nr || '').trim()
    || 'ohne Fassungsnamen';

// ═══════════════════════════════════════════════════════════════════════════

interface Props {
    /** Kopfzeile der Seite, üblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fußzeile der Seite, üblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    /** Der Kopfsatz des Handbuchs. `null` heißt „es gibt noch keins" und ist
     *  ein gültiger Zustand, kein Fehler. */
    handbuch: Handbuch | null;
    /**
     * Die Planfassungen des Projekts (`sige_projekte`, gleiche `project_id`,
     * nicht gelöscht). Optional: das Handbuch ist auch ohne Plan les- und
     * druckbar. Fehlt die Liste, sagt das Blatt das ausdrücklich, statt
     * „keine Planfassung" zu behaupten.
     */
    planFassungen?: Planfassung[];
    /** `true`, wenn die Planfassungen gar nicht geladen wurden. */
    planUngeladen?: boolean;
}

export const Historie: React.FC<Props> = ({
    kopf, fuss, handbuch, planFassungen = [], planUngeladen = false,
}) => {
    const eintraege = sortiereHistorie(handbuch?.historie || []);
    const fassungen = sortierePlanfassungen(planFassungen || []);
    const freigegeben = handbuch?.status === 'freigegeben';

    return (
        <A3Seite kopf={kopf} fuss={fuss}>
            <div style={{ marginBottom: '6px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>
                    ÄNDERUNGSHISTORIE
                </div>
                <div style={{ fontSize: '8px', color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}>
                    Eine Freigabe friert die Kapitel ein. Eine spätere Änderung erzeugt eine neue
                    Fassung und überschreibt die freigegebene nicht.
                </div>
            </div>

            {/* Der aktuelle Stand als Kopfzeile — er ist die Aussage, wegen der
                jemand diese Seite aufschlägt. */}
            <div
                className="flex flex-wrap gap-x-6 shrink-0"
                style={{
                    border: `1px solid ${HANDBUCH_FARBEN.linie}`,
                    background: HANDBUCH_FARBEN.abschnittHintergrund,
                    padding: '3px 8px',
                    marginBottom: '6px',
                    fontSize: '9px',
                }}
            >
                <span><strong>Stand:</strong> {handbuch?.stand || '—'}</span>
                <span><strong>Fassung:</strong> {handbuch?.version_label || '—'}</span>
                <span>
                    <strong>Status:</strong>{' '}
                    <span style={{ color: freigegeben ? HANDBUCH_FARBEN.statusOk : HANDBUCH_FARBEN.statusOffen, fontWeight: 700 }}>
                        {freigegeben ? 'freigegeben' : 'Entwurf'}
                    </span>
                </span>
                {freigegeben && (
                    <span>
                        <strong>Freigegeben:</strong>{' '}
                        {formatDatum(handbuch?.freigegeben_am) || '—'}
                        {handbuch?.freigegeben_von_name ? ` durch ${handbuch.freigegeben_von_name}` : ''}
                    </span>
                )}
                <span>
                    <strong>Gültig:</strong>{' '}
                    {handbuch?.gueltig_von || handbuch?.gueltig_bis
                        ? `${formatDatum(handbuch?.gueltig_von) || '…'} – ${formatDatum(handbuch?.gueltig_bis) || 'unbefristet'}`
                        : 'nicht festgelegt'}
                </span>
                {/* Die Katalogfassung gehört auf dieses Blatt: sie sagt, unter
                    welchen Regeln beurteilt wurde. Wächst der Katalog später,
                    ändert das nicht, was hier stand. */}
                <span><strong>Katalog:</strong> {handbuch?.katalog_version || '—'}</span>
            </div>

            <div className="flex-1 min-h-0 flex flex-col gap-[6px]">
                <Abschnitt titel="Fassungen des Handbuchs" zusatz={`${eintraege.length}`} className="flex-1">
                    {eintraege.length === 0 ? (
                        <AbschnittLeer
                            text={handbuch
                                ? 'Noch keine Fassung vermerkt — das Handbuch ist in seiner ersten Bearbeitung.'
                                : 'Für dieses Projekt ist noch kein Handbuch angelegt.'}
                        />
                    ) : (
                        <AbschnittTabelle
                            kopf={['Fassung', 'Datum', 'Autor', 'Änderungen']}
                            breiten={['10%', '12%', '20%', '58%']}
                        >
                            {eintraege.map((e, i) => (
                                <AbschnittZeile key={`${e.nr}-${e.datum}-${i}`} erste={i === 0}>
                                    <AbschnittZelle style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {e.nr}
                                        {e.version_label && (
                                            <div style={{ fontWeight: 400, fontSize: '7px', color: HANDBUCH_FARBEN.textLeise }}>
                                                {e.version_label}
                                            </div>
                                        )}
                                    </AbschnittZelle>
                                    <AbschnittZelle style={{ whiteSpace: 'nowrap' }}>
                                        {formatDatum(e.datum) || e.datum}
                                    </AbschnittZelle>
                                    <AbschnittZelle>{e.autor || '—'}</AbschnittZelle>
                                    <AbschnittZelle>{e.aenderungen || '—'}</AbschnittZelle>
                                </AbschnittZeile>
                            ))}
                        </AbschnittTabelle>
                    )}
                </Abschnitt>

                <Abschnitt
                    titel="Zugrunde liegende Planfassungen (SiGe-Plan)"
                    zusatz={planUngeladen ? undefined : `${fassungen.length}`}
                    className="flex-1"
                >
                    {planUngeladen ? (
                        /* „Nicht geladen" und „nicht vorhanden" sind zwei
                           verschiedene Aussagen. Nur eine davon darf das
                           Dokument treffen. */
                        <AbschnittLeer text="Die Planfassungen wurden zu dieser Ansicht nicht geladen — die Liste ist nicht leer, sie ist unbekannt." />
                    ) : fassungen.length === 0 ? (
                        <AbschnittLeer text="Zu diesem Projekt ist kein SiGe-Plan angelegt. Zeiträume, Gefährdungen und Schnittstellen des Handbuchs haben dann keine Quelle." />
                    ) : (
                        <AbschnittTabelle
                            kopf={['Fassung', 'Plan-Nr.', 'Angelegt am', 'Status']}
                            breiten={['34%', '20%', '23%', '23%']}
                        >
                            {fassungen.map((p, i) => (
                                <AbschnittZeile key={p.id} erste={i === 0}>
                                    <AbschnittZelle style={{ fontWeight: i === 0 ? 700 : 400 }}>
                                        {fassungsName(p)}
                                        {i === 0 && (
                                            <span style={{ color: HANDBUCH_FARBEN.textLeise, fontWeight: 400 }}>
                                                {' '}— aktuell verwendet
                                            </span>
                                        )}
                                    </AbschnittZelle>
                                    <AbschnittZelle>{p.plan_nr || '—'}</AbschnittZelle>
                                    <AbschnittZelle>
                                        {formatDatum((p.created_at || '').slice(0, 10)) || '—'}
                                    </AbschnittZelle>
                                    <AbschnittZelle>{p.status || '—'}</AbschnittZelle>
                                </AbschnittZeile>
                            ))}
                        </AbschnittTabelle>
                    )}
                </Abschnitt>
            </div>

            <div
                style={{
                    borderTop: `1px solid ${HANDBUCH_FARBEN.linie}`,
                    marginTop: '5px',
                    paddingTop: '3px',
                    fontSize: '7.5px',
                    color: HANDBUCH_FARBEN.textLeise,
                }}
            >
                Die beiden Listen zählen getrennt: links die Fassungen dieses Handbuchs, rechts die
                Fassungen des SiGe-Plans, auf dem es beruht. Eine neue Planfassung macht das Handbuch
                nicht automatisch ungültig — sie ist der Anlass, es nachzuführen und dabei eine neue
                Fassung zu eröffnen.
            </div>
        </A3Seite>
    );
};
